import { useEffect } from 'react';
import type { ReactNode } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useSession } from '../lib/session';
import { ConnectionCard } from '../components/ConnectionCard';
import { Badge, Banner } from '../components/ui';
import {
  IconArrowRight,
  IconChecklist,
  IconExport,
  IconMatch,
  IconPhoto,
  IconQuote,
} from '../components/icons';

interface Tool {
  to: string;
  title: string;
  description: string;
  icon: (props: { className?: string }) => ReactNode;
  badge: { tone: 'green' | 'amber' | 'slate' | 'accent'; label: string };
  featured?: boolean;
  /** Admin-only tools are hidden from normal users' dashboard. */
  admin?: boolean;
}

export function Dashboard() {
  const { user, connections, refresh } = useSession();
  const [params, setParams] = useSearchParams();

  const scConnected = params.get('sc') === 'connected';
  const scError = params.get('sc_error');

  // After returning from the OAuth redirect, refresh connection state once.
  useEffect(() => {
    if (scConnected || scError) {
      refresh();
      const next = new URLSearchParams(params);
      next.delete('sc');
      next.delete('sc_error');
      setParams(next, { replace: true });
    }
  }, []);

  const anyReady = connections.some((c) => c.canSubmit);
  const greeting = user?.email?.split('@')[0] ?? 'there';

  const allTools: Tool[] = [
    {
      to: '/submit-quote',
      title: 'Submit Quote',
      description: 'Upload a quote PDF, confirm the work order, and submit it to the platform.',
      icon: IconQuote,
      badge: anyReady
        ? { tone: 'green', label: 'Ready' }
        : { tone: 'amber', label: 'Needs connection' },
      featured: true,
    },
    {
      to: '/corrigo-checklist',
      title: 'Corrigo Checklist',
      description:
        'Pull a work order, review procedures and hidden step instructions, download a vendor PDF, and push checklist responses.',
      icon: IconChecklist,
      badge: { tone: 'accent', label: 'Corrigo' },
    },
    // Upload Documents tool is hidden — its job is now covered by the "Include PDF
    // Copy" option on Submit Quote. The page remains routable at /documents.
    {
      to: '/photo-manager',
      title: 'Photo Manager',
      description:
        'Import photos (HEIC and more), a folder, or a .zip, convert them to JPG, rotate and pick the keepers, then download as a ZIP or upload to a Corrigo work order.',
      icon: IconPhoto,
      badge: { tone: 'accent', label: 'Corrigo' },
    },
    {
      to: '/data-export',
      title: 'Data Export',
      description:
        'Export a client’s Locations, Trades, and Priorities to CSV from Corrigo or ServiceChannel.',
      icon: IconExport,
      badge: { tone: 'slate', label: 'Corrigo · SC' },
      admin: true,
    },
    {
      to: '/location-match',
      title: 'Location Match',
      description:
        'Reconcile a platform location list against the FIT master list and download a MATCHED workbook.',
      icon: IconMatch,
      badge: { tone: 'slate', label: 'Corrigo · SC' },
      admin: true,
    },
  ];
  const tools = allTools.filter((tool) => !tool.admin || user?.isAdmin);

  return (
    <div className="space-y-10">
      {/* Hero */}
      <div>
        <h1 className="text-2xl font-semibold tracking-tight text-slate-900">
          Welcome back, <span className="capitalize">{greeting}</span>
        </h1>
        <p className="mt-1 text-sm text-slate-500">
          Submit quotes, manage checklists, and reconcile platform data — all in one place.
        </p>
      </div>

      {scConnected && <Banner tone="success">ServiceChannel connected.</Banner>}
      {scError && (
        <Banner tone="error">ServiceChannel connection failed: {scError.replace(/_/g, ' ')}.</Banner>
      )}

      {/* Connections */}
      <section>
        <div className="mb-3 flex items-center justify-between">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500">Connections</h2>
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          {connections.map((c) => (
            <ConnectionCard key={c.platform} status={c} onChange={refresh} />
          ))}
        </div>
      </section>

      {/* Tools */}
      <section>
        <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-slate-500">Tools</h2>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {tools.map((tool) => (
            <ToolCard key={tool.to} tool={tool} />
          ))}
        </div>
      </section>
    </div>
  );
}

function ToolCard({ tool }: { tool: Tool }) {
  const Icon = tool.icon;
  return (
    <Link
      to={tool.to}
      className={`group flex flex-col rounded-2xl border bg-white p-5 shadow-card transition duration-200 hover:-translate-y-0.5 hover:shadow-card-hover ${
        tool.featured ? 'border-accent-200 ring-1 ring-accent-100' : 'border-slate-200/80'
      }`}
    >
      <div className="mb-3 flex items-center justify-between">
        <span
          className={`flex h-11 w-11 items-center justify-center rounded-xl ${
            tool.featured
              ? 'bg-accent-600 text-white'
              : 'bg-accent-50 text-accent-600 ring-1 ring-inset ring-accent-100'
          }`}
        >
          <Icon className="h-5 w-5" />
        </span>
        <Badge tone={tool.badge.tone}>{tool.badge.label}</Badge>
      </div>
      <h3 className="text-base font-semibold text-slate-900">{tool.title}</h3>
      <p className="mt-1 flex-1 text-sm text-slate-500">{tool.description}</p>
      <span className="mt-4 inline-flex items-center gap-1 text-sm font-medium text-accent-700">
        Open tool
        <IconArrowRight className="h-4 w-4 transition group-hover:translate-x-0.5" />
      </span>
    </Link>
  );
}
